import React, { useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Popover,
  Typography,
  Slider,
} from "@mui/material";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ChromePicker } from "react-color";
import { toast } from "react-toastify";
import * as messages from "../../../utils/constants";
import Palette from "./Palette";
import dayjs from "dayjs";
import { MRT_EditActionButtons as ActioButtons } from "material-react-table";
import styled from "styled-components";
import ItemSelectChip from "./ItemSelectChip";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CustomEditActionButtons from "./EditActionButtons";

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-input {
    padding: 0;
    height: 0;
    visibility: hidden;
  }
`;

function EditRowDialog({
  table,
  row,
  itemData,
  initialData,
  handleUpdateOrder,
}) {
  const [newRow, setNewRow] = useState({
    order_name: initialData.order_name || "",
    item_id: initialData.item_id || "",
    qty: initialData.qty || "",
    priority: initialData.order_priority || 1,
    start_date: initialData.start_date ? dayjs(initialData.start_date) : null,
    due_date: initialData.due_date ? dayjs(initialData.due_date) : null,
    color: initialData.color || "#000000",
  });
  const [activeColor, setActiveColor] = useState(initialData.color);
  const [anchorEl, setAnchorEl] = useState(null);
  const onTileClick = (color) => {
    handleInputChange("color", color);
    toast.success(messages.successColorSelect);
    setActiveColor(color);
  };
  const setEditingRow = (field, value) => {
    table.setEditingRow({
      ...row,
      [field]: value,
    });
  };
  const handleInputChange = (field, value) => {
    setNewRow((prev) => ({ ...prev, [field]: value }));
    setEditingRow(field, value);
    setActiveColor(value);
  };
  const handleOpenChromePicker = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseChromePicker = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  return (
    <>
      <DialogTitle
        variant="h5"
        sx={{ fontSize: "18px !important", color: "#111" }}
      >
        생산계획 수정
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
      >
        <TextField
          label="주문명"
          value={newRow.order_name}
          onChange={(e) => handleInputChange("order_name", e.target.value)}
          fullWidth
        />
        <ItemSelectChip
          selectedItem={
            newRow.item_id
              ? [
                  itemData.filter((c) => c.item_id === newRow.item_id)[0]
                    .item_name,
                ]
              : []
          }
          setSelectedItem={(items) => handleInputChange("item_id", items[0])}
          itemList={itemData}
          disabledProps={true}
        />
        <TextField
          label="수량"
          type="number"
          value={newRow.qty}
          onChange={(e) => handleInputChange("qty", e.target.value)}
          fullWidth
        />
        <StyledTextField
          label="우선순위"
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <Box sx={{ width: "1900%", px: 1, py: 2 }}>
                <Slider
                  value={newRow.priority}
                  onChange={(_, value) => handleInputChange("priority", value)}
                  min={1}
                  max={10}
                  step={1}
                  valueLabelDisplay="off"
                  sx={{
                    "& .MuiSlider-rail": {
                      opacity: 0.3,
                    },
                    "& .MuiSlider-thumb": {
                      width: 16,
                      height: 16,
                    },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 1,
                    mt: 1,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    "& .selected": {
                      fontWeight: 700,
                      color: "text.primary",
                    },
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <span
                      key={num}
                      className={num === newRow.priority ? "selected" : ""}
                    >
                      {num}
                    </span>
                  ))}
                </Box>
              </Box>
            ),
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MobileDateTimePicker
            label="시작 시간"
            value={newRow.start_date}
            onChange={(date) => handleInputChange("start_date", date)}
            format="YYYY년 MM월 DD일 HH시 mm분"
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MobileDateTimePicker
            label="납기일"
            value={newRow.due_date}
            onChange={(date) => handleInputChange("due_date", date)}
            format="YYYY년 MM월 DD일 HH시 mm분"
          />
        </LocalizationProvider>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            색상 선택
          </Typography>
          <Palette
            handleOpenChromePicker={handleOpenChromePicker}
            title="더 많은 색상 선택"
            handleInputChange={handleInputChange}
            colors1={[
              { value: "#96bef0" },
              { value: "#ffb5ea" },
              { value: "#8affad" },
              { value: "#9ccdff" },
              { value: "#ea9cfb", lightLabel: true },
              { value: "#f0be68", lightLabel: true },
              { value: "#64f0f0", lightLabel: true },
              { value: "#efb9b9", lightLabel: true },
              { value: "#6ff080", lightLabel: true },
              { value: "#b7e1f0", lightLabel: true },
            ]}
            colors2={[
              { value: "#ffd179" },
              { value: "#c5ff7b" },
              { value: "#64b9ff" },
              { value: "#d87570" },
              { value: "#aff3b2", lightLabel: true },
              { value: "#a2a6d8", lightLabel: true },
              { value: "#ffbe96", lightLabel: true },
              { value: "#c882e2", lightLabel: true },
              { value: "#c4a9d3", lightLabel: true },
              { value: "#aad2e8", lightLabel: true },
            ]}
            activeColor={activeColor}
            setActiveColor={setActiveColor}
            onTileClick={onTileClick}
          />
        </Box>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseChromePicker}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box sx={{ p: 2, backgroundColor: "rgba(0, 0, 0, 0.6)" }}>
            <ChromePicker
              color={newRow.color}
              onChangeComplete={(color) => {
                handleInputChange("color", color.hex);
                handleCloseChromePicker();
              }}
            />
          </Box>
        </Popover>
      </DialogContent>
      <DialogActions>
        {/* <ActioButtons variant="text" table={table} row={row} /> */}
        <CustomEditActionButtons
          table={table}
          row={row}
          handleUpdateOrder={handleUpdateOrder}
        />
      </DialogActions>
    </>
  );
}

export default EditRowDialog;
